import { Box, CircularProgress, Pagination, Typography } from '@mui/material';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import './GigReviewsSection.scss';
import { useEffect, useState } from 'react';
import { Review } from '../../../../Models/Review';
import { ReviewAPIHandler } from '../../../../services/NetworkClient/BackendAPIs/ReviewAPIHandler';
import GigReviewItem from './GigReviewItem';

interface GigReviewsSectionProps {
    gigId: string;
    totalGigRatings: number;
}

const GigReviewsSection = ({ gigId, totalGigRatings }: GigReviewsSectionProps) => {
    const pageSize = 5;

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [reviews, setReviews] = useState<Review[]>([]);
    const [page, setPage] = useState(1); // Page numbers start from 1

    const totalPages = Math.ceil(totalGigRatings / pageSize);

    const fetchReviews = async (pageNumber: number) => {
        try {
            setLoading(true);
            setError("");
            const fetchedReviews = await ReviewAPIHandler.shared.getReviewsByGig(gigId, pageNumber, pageSize);
            setReviews(fetchedReviews);
        } catch (err) {
            setError(err as string);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReviews(page);

        // eslint-disable-next-line
    }, [page]);

    const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
        setPage(value);
    };

    const GigReviewsContainer = () => {
        if (reviews.length <= 0) {
            return (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 6 }}>
                    <StarBorderIcon sx={{ fontSize: 80, color: '#e0e0e0', mb: 2 }} />
                    <Typography fontSize={{ xs: '16px', md: '20px' }} fontWeight={500} mb="8px" color="#333">
                        No Reviews found
                    </Typography>
                    <Typography fontSize={{ xs: '14px', md: '16px' }} color="#999">
                        Be the first to review this gig
                    </Typography>
                </Box>
            );
        }

        return (
            <div>
                {reviews.map((review) => (
                    <GigReviewItem review={review} key={review.id} />
                ))}
                {totalPages > 1 && (
                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={handlePageChange}
                        sx={{
                            mt: 3,
                            display: 'flex',
                            justifyContent: 'center',
                            '& .MuiPaginationItem-root': {
                                fontWeight: 500,
                                fontFamily: 'Arial',
                            },
                            '& .Mui-selected': {
                                backgroundColor: 'black',
                                color: 'white',
                                '&:hover': {
                                    backgroundColor: '#222',
                                },
                            },
                        }}
                        color="primary"
                    />

                )}
            </div>
        );
    };

    return (
        <div className='gig-reviews-section'>
            <Typography variant='h4' fontSize={{ xs: '28px', md: '36px' }} fontWeight={1000} className='heading' mb="20px">
                Ratings & Reviews ({totalGigRatings})
            </Typography>
            {loading ?
                <Box height={"100%"} width={"100%"} className="center"> <CircularProgress size={64} thickness={5} /> </Box> :
                <GigReviewsContainer />
            }
            {
                error &&
                <Typography variant='h4' fontSize={{ xs: '14px', md: '18px' }} color={'error'} mb="20px">
                    {error}
                </Typography>
            }
        </div>
    );
};

export default GigReviewsSection;
